import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import BackButton from '../../components/BackButton';
import { toast } from 'sonner';
import {
  Plus, Search, FileDown, Mail, Send, Save, Trash2, ArrowLeft, Eye
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const money = (v) => (v ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const fmtDate = (d) => d ? d.split('-').reverse().join('/') : '-';

const statusColors = {
  borrador: 'bg-slate-100 text-slate-700', enviado: 'bg-blue-100 text-blue-800',
  confirmado: 'bg-emerald-100 text-emerald-800', recibido: 'bg-green-100 text-green-800',
  facturado: 'bg-violet-100 text-violet-800', cancelado: 'bg-red-100 text-red-800',
};

const emptyLine = () => ({ id: crypto.randomUUID(), concepto: '', cantidad: 1, precio_unitario: 0, iva_porcentaje: 21, unidad: 'ud' });

export const PurchaseOrderList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => { fetchList(); }, []);
  const fetchList = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_URL}/billing/purchase-orders`);
      setItems(r.data);
    } catch { toast.error('Error al cargar pedidos'); }
    finally { setLoading(false); }
  };

  const downloadPdf = async (po) => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/billing/purchase-orders/${po.id}/pdf`, {
        responseType: 'blob', headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const safe = (po.numero || 'borrador').replace(/[()\/]/g, '').replace(/\s+/g, '') || 'borrador';
      a.download = `Pedido_${safe}.pdf`;
      a.click();
      toast.success('PDF descargado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al descargar PDF');
    }
  };

  const filtered = items.filter(d => !search || d.numero?.toLowerCase().includes(search.toLowerCase()) ||
    d.supplier_data?.nombre_fiscal?.toLowerCase().includes(search.toLowerCase()));

  return (
    <Layout>
      <div className="space-y-6" data-testid="purchase-orders-list">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <BackButton to="/billing" label="Volver a Intranet" />
            <h1 className="text-3xl font-manrope font-bold text-slate-900">Pedidos a proveedores</h1>
            <p className="text-slate-500 mt-1">Órdenes de compra ({items.length})</p>
          </div>
          <Button onClick={() => navigate('/billing/purchase-orders/new')} className="bg-[#004E89] hover:bg-[#003a68]" data-testid="new-po-button">
            <Plus className="w-4 h-4 mr-2" /> Nuevo pedido
          </Button>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input className="pl-9" placeholder="Buscar..." value={search} onChange={e=>setSearch(e.target.value)} />
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          {loading ? <div className="p-12 text-center text-slate-500">Cargando...</div>
            : filtered.length === 0 ? <div className="p-12 text-center text-slate-500">Sin pedidos.</div>
            : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Nº</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Fecha</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Proveedor</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Entrega</th>
                      <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase">Total</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Estado</th>
                      <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(d => (
                      <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-3 text-sm font-mono">{d.numero}</td>
                        <td className="p-3 text-sm">{fmtDate(d.fecha_emision)}</td>
                        <td className="p-3 text-sm">{d.supplier_data?.nombre_fiscal}<div className="text-xs text-slate-500">{d.supplier_data?.cif_nif}</div></td>
                        <td className="p-3 text-sm">{fmtDate(d.fecha_entrega_prevista)}</td>
                        <td className="p-3 text-sm text-right font-medium">{money(d.total)}</td>
                        <td className="p-3"><span className={`inline-block text-xs px-2 py-1 rounded-full capitalize ${statusColors[d.estado] || 'bg-slate-100'}`}>{d.estado}</span></td>
                        <td className="p-3 text-right space-x-1">
                          <button onClick={()=>navigate(`/billing/purchase-orders/${d.id}`)} title="Ver/Editar" className="p-1 text-slate-600 hover:text-blue-600"><Eye className="w-4 h-4 inline" /></button>
                          <button onClick={()=>downloadPdf(d)} title="Descargar PDF" data-testid={`download-po-pdf-${d.id}`} className="p-1 text-slate-600 hover:text-blue-600"><FileDown className="w-4 h-4 inline" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>
      </div>
    </Layout>
  );
};


export const PurchaseOrderEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [loading, setLoading] = useState(!isNew);
  const [doc, setDoc] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [shippingAddrs, setShippingAddrs] = useState([]);
  const [form, setForm] = useState({
    supplier_id: '', fecha_emision: new Date().toISOString().slice(0, 10),
    fecha_entrega_prevista: '', referencia_proveedor: '',
    direccion_entrega: '', notas: '', lineas: [emptyLine()],
    no_incluir_albaran_cotizado: false,
  });

  useEffect(() => {
    (async () => {
      const [r, ra] = await Promise.all([
        axios.get(`${API_URL}/billing/suppliers?activo=true`),
        axios.get(`${API_URL}/billing/shipping-addresses`).catch(() => ({ data: [] })),
      ]);
      setSuppliers(r.data);
      setShippingAddrs(ra.data || []);
      if (isNew) {
        // Precarga dirección predeterminada
        const def = (ra.data || []).find(a => a.is_default);
        if (def) {
          setForm(f => ({ ...f, direccion_entrega: def.direccion_completa }));
        }
      } else {
        try {
          const res = await axios.get(`${API_URL}/billing/purchase-orders/${id}`);
          setDoc(res.data);
          setForm({
            supplier_id: res.data.supplier_id, fecha_emision: res.data.fecha_emision,
            fecha_entrega_prevista: res.data.fecha_entrega_prevista || '',
            referencia_proveedor: res.data.referencia_proveedor || '',
            direccion_entrega: res.data.direccion_entrega || '',
            notas: res.data.notas || '',
            lineas: (res.data.lineas || []).map(l => ({ ...l })),
            no_incluir_albaran_cotizado: !!res.data.no_incluir_albaran_cotizado,
          });
        } catch { toast.error('No encontrado'); navigate('/billing/purchase-orders'); }
        finally { setLoading(false); }
      }
    })();
  }, [id, isNew, navigate]);

  const editable = !doc || doc.estado === 'borrador';
  const set = (k,v)=>setForm(p=>({...p,[k]:v}));
  const addLine = () => set('lineas',[...form.lineas, emptyLine()]);
  const removeLine = (i) => set('lineas', form.lineas.filter((_,idx)=>idx!==i));
  const updateLine = (i, patch) => { const n=[...form.lineas]; n[i]={...n[i],...patch}; set('lineas',n); };

  const totals = React.useMemo(() => {
    let base=0, iva=0;
    for (const l of form.lineas) {
      const c=Number(l.cantidad)||0, p=Number(l.precio_unitario)||0, ivap=Number(l.iva_porcentaje)||0;
      const sub = c*p;
      base += sub;
      iva += sub * ivap / 100;
    }
    return { base_imponible: base, total_iva: iva, total: base+iva };
  }, [form.lineas]);

  const save = async () => {
    if (!form.supplier_id) return toast.error('Selecciona un proveedor');
    if (form.lineas.filter(l=>l.concepto).length===0) return toast.error('Añade al menos una línea');
    const payload = {
      ...form,
      lineas: form.lineas.filter(l=>l.concepto).map(l => ({
        id:l.id, concepto:l.concepto, cantidad:Number(l.cantidad)||0,
        precio_unitario:Number(l.precio_unitario)||0, iva_porcentaje:Number(l.iva_porcentaje)||0,
        unidad: l.unidad || 'ud', descuento_porcentaje: 0,
      })),
    };
    try {
      if (isNew) {
        const r = await axios.post(`${API_URL}/billing/purchase-orders`, payload);
        toast.success('Borrador guardado');
        navigate(`/billing/purchase-orders/${r.data.id}`, { replace: true });
      } else {
        const r = await axios.put(`${API_URL}/billing/purchase-orders/${id}`, payload);
        setDoc(r.data); toast.success('Guardado');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const issue = async () => {
    if (!window.confirm('¿Emitir pedido? Se asignará número definitivo.')) return;
    await save();
    try {
      const r = await axios.post(`${API_URL}/billing/purchase-orders/${id || doc?.id}/issue`);
      setDoc(r.data); toast.success(`Emitido: ${r.data.numero}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openPdf = () => {
    if (!doc?.id) return;
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/billing/purchase-orders/${doc.id}/pdf`, { responseType:'blob', headers:{ Authorization: `Bearer ${token}` }})
      .then(r=>{
        const b=new Blob([r.data],{type:'application/pdf'});
        window.open(URL.createObjectURL(b),'_blank');
      });
  };

  const downloadPdf = () => {
    if (!doc?.id) return;
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/billing/purchase-orders/${doc.id}/pdf`, { responseType:'blob', headers:{ Authorization: `Bearer ${token}` }})
      .then(r=>{
        const b=new Blob([r.data],{type:'application/pdf'});
        const a=document.createElement('a');
        a.href = URL.createObjectURL(b);
        a.download = `Pedido_${(doc.numero||'').replace('/','-')}.pdf`;
        a.click();
        toast.success('PDF descargado');
      })
      .catch(() => toast.error('Error al descargar PDF'));
  };

  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: '', subject: '', body: '' });

  const openEmailDialog = () => {
    setEmailForm({
      to: doc?.supplier_data?.email || '',
      subject: `Pedido ${doc?.numero || ''} - IRTA Asistencia Médica`,
      body: ''
    });
    setShowEmailDialog(true);
  };

  const sendEmail = async () => {
    if (!emailForm.to) return toast.error('Falta destinatario');
    try {
      await axios.post(`${API_URL}/billing/purchase-orders/${doc.id}/email`, emailForm);
      toast.success('Email en cola de envío');
      setShowEmailDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar');
    }
  };

  const changeStatus = async (estado) => {
    try {
      const r = await axios.patch(`${API_URL}/billing/purchase-orders/${doc.id}/status`, { estado });
      setDoc(r.data); toast.success('Estado actualizado');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  if (loading) return <Layout><div className="p-12 text-center text-slate-500">Cargando...</div></Layout>;

  return (
    <Layout>
      <div className="space-y-6" data-testid="po-editor">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={()=>navigate('/billing/purchase-orders')} className="text-slate-500 hover:text-slate-900"><ArrowLeft className="w-5 h-5" /></button>
            <div>
              <h1 className="text-2xl font-manrope font-bold text-slate-900">Pedido {doc?.numero && doc.numero !== '(borrador)' ? `#${doc.numero}` : ''}</h1>
              {doc?.estado && <span className="inline-block mt-1 text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700 capitalize">{doc.estado}</span>}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {editable && <Button onClick={save} variant="outline" data-testid="po-save"><Save className="w-4 h-4 mr-1" /> Guardar</Button>}
            {doc && doc.estado === 'borrador' && <Button onClick={issue} className="bg-[#004E89] hover:bg-[#003a68]" data-testid="po-issue"><Send className="w-4 h-4 mr-1" /> Emitir</Button>}
            {doc && (
              <>
                <Button onClick={openPdf} variant="outline" data-testid="po-view-pdf"><Eye className="w-4 h-4 mr-1" /> Ver PDF</Button>
                <Button onClick={downloadPdf} variant="outline" data-testid="po-download-pdf"><FileDown className="w-4 h-4 mr-1" /> Descargar</Button>
                {doc.estado !== 'borrador' && (
                  <>
                    <Button onClick={openEmailDialog} variant="outline" data-testid="po-send-email"><Mail className="w-4 h-4 mr-1" /> Enviar por email</Button>
                    {['confirmado','recibido','facturado','cancelado'].map(s => (
                      <Button key={s} size="sm" variant="outline" onClick={()=>changeStatus(s)}>{s}</Button>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label>Proveedor *</Label>
            <select disabled={!editable} value={form.supplier_id} onChange={e=>set('supplier_id', e.target.value)} className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm">
              <option value="">— Selecciona —</option>
              {suppliers.map(s=><option key={s.id} value={s.id}>{s.nombre_fiscal} ({s.cif_nif})</option>)}
            </select>
          </div>
          <div><Label>Fecha</Label><Input disabled={!editable} type="date" value={form.fecha_emision} onChange={e=>set('fecha_emision', e.target.value)} /></div>
          <div><Label>Entrega prevista</Label><Input disabled={!editable} type="date" value={form.fecha_entrega_prevista} onChange={e=>set('fecha_entrega_prevista', e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Referencia proveedor</Label><Input disabled={!editable} value={form.referencia_proveedor} onChange={e=>set('referencia_proveedor', e.target.value)} /></div>
        </div>

        {/* Envío */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="po-shipping-section">
          <h3 className="font-manrope font-semibold text-slate-900">Envío</h3>
          {editable && shippingAddrs.length > 0 && (
            <div>
              <Label>Cargar dirección predefinida</Label>
              <select
                data-testid="po-preset-addr"
                className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
                value=""
                onChange={e => {
                  const a = shippingAddrs.find(x => x.id === e.target.value);
                  if (a) set('direccion_entrega', a.direccion_completa);
                }}
              >
                <option value="">— Selecciona una dirección guardada —</option>
                {shippingAddrs.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.alias}{a.is_default ? ' ★ (predeterminada)' : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">Puedes gestionarlas desde Ajustes de facturación → Direcciones predefinidas.</p>
            </div>
          )}
          <div>
            <Label>Dirección de envío</Label>
            <textarea
              disabled={!editable}
              value={form.direccion_entrega}
              onChange={e=>set('direccion_entrega', e.target.value)}
              className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
              rows={3}
              placeholder="Indica la dirección donde el proveedor debe enviar el material"
              data-testid="po-direccion-entrega"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 select-none">
            <input
              type="checkbox"
              disabled={!editable}
              checked={!!form.no_incluir_albaran_cotizado}
              onChange={e=>set('no_incluir_albaran_cotizado', e.target.checked)}
              className="w-4 h-4 rounded border-slate-300"
              data-testid="po-no-albaran-cotizado"
            />
            <span>No incluir albarán cotizado (valorado) con la entrega — aparecerá como <b>aviso destacado</b> en el PDF</span>
          </label>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-manrope font-semibold text-slate-900">Líneas</h3>
            {editable && <Button onClick={addLine} size="sm" variant="outline"><Plus className="w-4 h-4 mr-1" /> Añadir</Button>}
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase">
              <tr className="border-b">
                <th className="text-left p-2">Concepto</th>
                <th className="text-right p-2 w-20">Cant.</th>
                <th className="text-right p-2 w-24">Precio</th>
                <th className="text-right p-2 w-16">IVA%</th>
                <th className="text-right p-2 w-28">Subtotal</th>
                {editable && <th className="w-8"></th>}
              </tr>
            </thead>
            <tbody>
              {form.lineas.map((l,idx)=>{
                const sub=(Number(l.cantidad)||0)*(Number(l.precio_unitario)||0);
                return (
                  <tr key={l.id} className="border-b">
                    <td className="p-1"><Input disabled={!editable} value={l.concepto} onChange={e=>updateLine(idx,{concepto:e.target.value})} /></td>
                    <td className="p-1"><Input disabled={!editable} type="number" step="0.01" value={l.cantidad} onChange={e=>updateLine(idx,{cantidad:e.target.value})} className="text-right" /></td>
                    <td className="p-1"><Input disabled={!editable} type="number" step="0.01" value={l.precio_unitario} onChange={e=>updateLine(idx,{precio_unitario:e.target.value})} className="text-right" /></td>
                    <td className="p-1">
                      <select disabled={!editable} value={l.iva_porcentaje} onChange={e=>updateLine(idx,{iva_porcentaje:parseFloat(e.target.value)})} className="w-full rounded-md border border-slate-300 px-2 py-1 text-xs text-right">
                        <option value={0}>0%</option><option value={4}>4%</option><option value={10}>10%</option><option value={21}>21%</option>
                      </select>
                    </td>
                    <td className="p-2 text-right font-medium">{money(sub)}</td>
                    {editable && <td className="p-1"><button onClick={()=>removeLine(idx)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button></td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <Label>Notas</Label>
            <textarea disabled={!editable} value={form.notas} onChange={e=>set('notas', e.target.value)} className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" rows={4} />
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h3 className="font-manrope font-semibold text-slate-900 mb-3">Totales</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Base imponible:</span><span>{money(totals.base_imponible)}</span></div>
              <div className="flex justify-between"><span>IVA:</span><span>{money(totals.total_iva)}</span></div>
              <div className="border-t pt-2 flex justify-between text-lg"><span className="font-bold">TOTAL:</span><span className="font-bold text-[#004E89]">{money(totals.total)}</span></div>
            </div>
          </div>
        </div>

        {/* Dialog envío email */}
        <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>Enviar pedido por email</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Para *</Label><Input type="email" value={emailForm.to} onChange={e=>setEmailForm(p=>({...p, to: e.target.value}))} required data-testid="po-email-to" /></div>
              <div><Label>Asunto</Label><Input value={emailForm.subject} onChange={e=>setEmailForm(p=>({...p, subject: e.target.value}))} /></div>
              <div><Label>Mensaje (opcional, HTML)</Label><textarea value={emailForm.body} onChange={e=>setEmailForm(p=>({...p, body: e.target.value}))} className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" rows={5} placeholder="Se generará automáticamente si lo dejas vacío" /></div>
              <p className="text-xs text-slate-500">El PDF del pedido se adjunta automáticamente. Se envía copia (CC) a la empresa.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={()=>setShowEmailDialog(false)}>Cancelar</Button>
                <Button onClick={sendEmail} className="bg-[#004E89] hover:bg-[#003a68]" data-testid="po-email-submit"><Mail className="w-4 h-4 mr-1" /> Enviar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
